import { APIProvider, useMapsLibrary } from "@vis.gl/react-google-maps";
import { useEffect, useRef, useState } from "react";
import { isLikelyGoogleMapsJavaScriptApiKey } from "../lib/google-maps-key";

export type PickedAddress = {
  placeId: string;
  line1: string;
  city: string;
  state: string;
  zip: string;
  formattedAddress: string;
};

type Props = {
  value: string;
  onTextChange: (v: string) => void;
  onPick: (picked: PickedAddress) => void;
  placeholder?: string;
  className?: string;
  id?: string;
  autoComplete?: string;
  inputMode?: "text" | "numeric";
};

const STREET_NUMBER = "street_number";
const ROUTE = "route";
const LOCALITY = "locality";
const SUBLOCALITY = "sublocality";
const POSTAL_TOWN = "postal_town";
const ADMIN_LEVEL_1 = "administrative_area_level_1";
const POSTAL_CODE = "postal_code";

type AddressComponent = {
  long_name: string;
  short_name: string;
  types: string[];
};

function findComponent(
  components: AddressComponent[] | undefined,
  type: string,
): AddressComponent | null {
  if (!components) return null;
  return components.find((c) => c.types.includes(type)) ?? null;
}

function extractFromComponents(components: AddressComponent[] | undefined): {
  line1: string;
  city: string;
  state: string;
  zip: string;
} {
  const streetNumber = findComponent(components, STREET_NUMBER)?.long_name ?? "";
  const route = findComponent(components, ROUTE)?.short_name ?? "";
  const line1 = [streetNumber, route].filter((s) => s.length > 0).join(" ").trim();
  const city =
    findComponent(components, LOCALITY)?.long_name ??
    findComponent(components, SUBLOCALITY)?.long_name ??
    findComponent(components, POSTAL_TOWN)?.long_name ??
    "";
  const state = findComponent(components, ADMIN_LEVEL_1)?.short_name ?? "";
  const zip = findComponent(components, POSTAL_CODE)?.long_name ?? "";
  return { line1, city, state, zip };
}

function GooglePlacesAutocompleteInputInner({
  value,
  onTextChange,
  onPick,
  placeholder,
  className,
  id,
  autoComplete,
  inputMode,
}: Props) {
  const placesLib = useMapsLibrary("places");
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!placesLib || !inputRef.current) return;
    const autocomplete = new placesLib.Autocomplete(inputRef.current, {
      types: ["address"],
      componentRestrictions: { country: "us" },
      fields: ["address_components", "place_id", "types", "formatted_address"],
    });
    const listener = autocomplete.addListener("place_changed", () => {
      const place = autocomplete.getPlace();
      if (!place.place_id) return;
      const parts = extractFromComponents(place.address_components as AddressComponent[] | undefined);
      onPick({
        placeId: place.place_id,
        line1: parts.line1,
        city: parts.city,
        state: parts.state,
        zip: parts.zip,
        formattedAddress: place.formatted_address ?? "",
      });
    });
    return () => {
      listener.remove();
    };
  }, [placesLib, onPick]);

  return (
    <input
      ref={inputRef}
      id={id}
      value={value}
      onChange={(e) => onTextChange(e.target.value)}
      placeholder={placeholder}
      className={className}
      autoComplete={autoComplete ?? "street-address"}
      inputMode={inputMode}
    />
  );
}

/**
 * Street-address input backed by Google Places Autocomplete. When the user
 * picks a suggestion, **`onPick`** fires with the normalized address parts
 * plus the Google **`place_id`** (used server-side by the private-residence
 * check). When the API key is missing or invalid, falls back to a plain
 * `<input>` so the form still works.
 */
export function GooglePlacesAutocompleteInput(props: Props) {
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY?.trim() ?? "";
  const keyOk = isLikelyGoogleMapsJavaScriptApiKey(apiKey);

  const [authRejected, setAuthRejected] = useState(false);
  useEffect(() => {
    if (!keyOk) return;
    const prev = window.gm_authFailure;
    window.gm_authFailure = () => {
      setAuthRejected(true);
      prev?.();
    };
    return () => {
      window.gm_authFailure = prev;
    };
  }, [keyOk]);

  if (!keyOk || authRejected) {
    return (
      <input
        value={props.value}
        onChange={(e) => props.onTextChange(e.target.value)}
        placeholder={props.placeholder}
        className={props.className}
        id={props.id}
        autoComplete={props.autoComplete ?? "street-address"}
        inputMode={props.inputMode}
      />
    );
  }

  return (
    <APIProvider apiKey={apiKey} libraries={["places"]}>
      <GooglePlacesAutocompleteInputInner {...props} />
    </APIProvider>
  );
}
